import { useEffect, useState } from 'react';
import {
  Box, Paper, Tabs, Tab, Typography, Checkbox, FormControlLabel,
  Button, Alert, TextField, Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useNavigate } from 'react-router-dom';
import {
  getGuardDutyConfig, updateFeatures, updateGDRegions, disableGuardDuty,
  startGDRolesScan, getGDRolesJob, fixGDRoles,
} from '../../../services/guarddutyApi';
import RolePermissionsPanel from '../../../components/portal/securityhub/RolePermissionsPanel';
import { AWS_REGIONS } from '../../../constants/awsRegions';
import MultiSelectField from './MultiSelectField';
import PortalPageHeader from '../../../components/portal/PortalPageHeader';
import { PortalLoading, PortalErrorState } from '../../../components/portal/PortalStates';
import OperationProgress from './OperationProgress';
import { POLL_INTERVAL_MS } from './pollInterval';
import { motion } from 'motion/react';
import { pageVariants, sectionVariants } from '../../../animations/variants';
import {
  CK, FONT_BODY, listCardSx, sectionCardSx, sectionHeadingSx, dialogProps, dialogTitleSx,
} from '../portalStyles';

// GuardDuty accent, shared with every other GuardDuty route.
const GD = '#E65100';
const GD_DARK = '#BF360C';

const primaryBtnSx = {
  backgroundColor: GD,
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
  mt: 1,
  '&:hover': { backgroundColor: GD_DARK },
};

const textBtnSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  color: CK.body,
};

const dangerBtnSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  borderRadius: '6px',
  px: 3,
};

const tabSx = {
  fontFamily: FONT_BODY,
  fontWeight: 600,
  textTransform: 'none',
  fontSize: '0.9rem',
  minHeight: 56,
  color: CK.body,
  '&.Mui-selected': { color: GD },
};

const bodyTextSx = { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.body };

const checkboxSx = { color: CK.disabled, '&.Mui-checked': { color: GD } };

const featureLabelSx = {
  display: 'block',
  '& .MuiFormControlLabel-label': { fontFamily: FONT_BODY, fontSize: '0.875rem', color: CK.navy },
};

const settingsHeader = (
  <PortalPageHeader
    icon={<SettingsIcon sx={{ fontSize: 24 }} />}
    title="Settings"
    subtitle="Manage protection plans and GuardDuty status"
    color={GD}
  />
);

// The config DTO carries these as JSON strings. Tolerate an already-decoded array
// so a DTO change cannot blank the form.
const toStringArray = (v) => {
  if (Array.isArray(v)) return v;
  try { return JSON.parse(v || '[]'); } catch (_) { return []; }
};

const sameSet = (a, b) => a.length === b.length
  && [...a].sort().join('|') === [...b].sort().join('|');

// UpdateLinkingRegions refuses outright unless the config is settled
// ("cannot update regions while config status is %s"), so a Save dispatched from
// any other status is a guaranteed error banner. This is that server rule, spelled
// out on the client so the button is dead instead of the request.
const configAllowsRegionUpdate = (status) => status === 'enabled' || status === 'partial_failure';

// GuardDuty protection plans. The raw enum names are what the backend
// updateFeatures expects.
const FEATURE_OPTIONS = [
  { id: 'S3_DATA_EVENTS', label: 'S3 Protection (data events)' },
  { id: 'EKS_AUDIT_LOGS', label: 'EKS Protection (audit logs)' },
  { id: 'EBS_MALWARE_PROTECTION', label: 'Malware Protection (EBS)' },
  { id: 'RDS_LOGIN_EVENTS', label: 'RDS Protection (login events)' },
  { id: 'LAMBDA_NETWORK_LOGS', label: 'Lambda Protection (network logs)' },
  { id: 'RUNTIME_MONITORING', label: 'Runtime Monitoring' },
];

export default function GuardDutySettingsPage() {
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(true);
  const [config, setConfig] = useState(null);
  const [features, setFeatures] = useState([]);
  const [regions, setRegions] = useState([]);
  const [confirm, setConfirm] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const c = await getGuardDutyConfig();
        const cfg = c?.data;
        setConfig(cfg);
        if (cfg) {
          // Same decoder the dirty check below uses, so "unchanged" cannot depend on
          // which of two parsers ran.
          setFeatures(toStringArray(cfg.enabledFeatures));
          setRegions(toStringArray(cfg.linkingRegions));
        }
      } catch (e) {
        setErr(e.message);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const operationRunning = config?.status === 'in_progress';

  // Both Saves are gated on a settled config, so without a poll a visitor who
  // arrives mid-operation would sit in front of permanently disabled buttons until
  // they reloaded by hand. Only the config is refreshed — never the two form
  // fields, which would otherwise overwrite an edit in progress.
  useEffect(() => {
    if (!operationRunning) return undefined;
    const id = setInterval(async () => {
      try {
        const c = await getGuardDutyConfig();
        setConfig(c?.data || null);
      } catch (_) { /* transient */ }
    }, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [operationRunning]);

  const saveFeatures = async () => {
    // Belt to the Save button's braces: the backend rejects an empty list with
    // "at least one feature must be enabled", which would surface as a bare error
    // banner after the user thought they had turned GuardDuty's plans off.
    if (features.length === 0) {
      setErr('Select at least one protection plan — GuardDuty cannot run with none.');
      return;
    }
    setBusy(true);
    try {
      await updateFeatures(features);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner until the update finishes.
      navigate('/securityhub/guardduty');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const saveRegions = async () => {
    if (regions.length === 0) {
      setErr('Select at least one linking region — GuardDuty cannot run with none.');
      return;
    }
    if (!regions.includes(config.homeRegion)) {
      setErr('Home region cannot be removed');
      return;
    }
    setBusy(true);
    try {
      await updateGDRegions(regions);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner while regions are enrolled/torn down.
      navigate('/securityhub/guardduty');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  const doDisable = async () => {
    setBusy(true);
    try {
      await disableGuardDuty();
      setConfirmOpen(false);
      setErr(null);
      // Hand off to the Dashboard entry — it polls config and renders the
      // in-progress banner during teardown, then the setup wizard once
      // status transitions to "disabled".
      navigate('/securityhub/guardduty');
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  // Loading keeps the header mounted and swaps only the card body, so the page
  // does not jump once the settings land.
  if (loading) {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Paper elevation={0} sx={sectionCardSx}>
            <PortalLoading label="Loading settings…" color={GD} />
          </Paper>
        </motion.div>
      </motion.div>
    );
  }
  if (!config || config.status === 'disabled') {
    return (
      <motion.div variants={pageVariants} initial="initial" animate="animate">
        <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
        <motion.div variants={sectionVariants}>
          <Alert
            severity="info"
            sx={{ borderRadius: '8px', fontFamily: FONT_BODY, '& .MuiAlert-message': { fontFamily: FONT_BODY } }}
            action={
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/guardduty')} sx={{ ...textBtnSx, color: 'inherit' }}>
                Go to setup
              </Button>
            }
          >
            GuardDuty is not set up yet for this customer. Complete the setup wizard first to manage protection plans or disable the service.
          </Alert>
        </motion.div>
      </motion.div>
    );
  }

  const storedFeatures = toStringArray(config.enabledFeatures);
  const storedRegions = toStringArray(config.linkingRegions);
  const featuresDirty = !sameSet(features, storedFeatures);
  const regionsDirty = !sameSet(regions, storedRegions);
  const canUpdateRegions = configAllowsRegionUpdate(config.status);
  // "failed" is terminal, not transitional: nothing is running, and the region
  // gate above refuses it until setup is re-run. Without saying so the Save button
  // is dead with no explanation whatsoever.
  const setupFailed = config.status === 'failed';

  return (
    <motion.div variants={pageVariants} initial="initial" animate="animate">
      <motion.div variants={sectionVariants}>{settingsHeader}</motion.div>
      {err && (
        <motion.div variants={sectionVariants}>
          <PortalErrorState error={err} />
        </motion.div>
      )}

      {operationRunning && (
        <motion.div variants={sectionVariants}>
          <OperationProgress
            percent={config.progress}
            label={config.progressLabel || 'A GuardDuty operation is running — settings are locked until it finishes'}
            sx={{ mb: 2 }}
          />
        </motion.div>
      )}

      {setupFailed && (
        <motion.div variants={sectionVariants}>
          <Alert
            severity="error"
            sx={{ mb: 2, borderRadius: '8px' }}
            action={(
              <Button color="inherit" size="small" onClick={() => navigate('/securityhub/guardduty')} sx={{ ...textBtnSx, color: 'inherit' }}>
                Re-run setup
              </Button>
            )}
          >
            GuardDuty setup failed, so linking regions cannot be changed until it is
            re-run. Disabling is still available from the Danger zone.
          </Alert>
        </motion.div>
      )}

      <motion.div variants={sectionVariants}>
      <Paper elevation={0} sx={listCardSx}>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
          sx={{
            borderBottom: `1px solid ${CK.border}`,
            '& .MuiTabs-indicator': { backgroundColor: GD, height: 3 },
          }}
        >
          <Tab label="Features" sx={tabSx} />
          <Tab label="Regions" sx={tabSx} />
          <Tab label="Permissions" sx={tabSx} />
          <Tab label="Danger zone" sx={tabSx} />
        </Tabs>

        {tab === 0 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Protection plans</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              Toggle the GuardDuty protection plans enabled across every member
              account and region.
            </Typography>
            {FEATURE_OPTIONS.map((f) => (
              <FormControlLabel
                key={f.id}
                control={
                  <Checkbox
                    sx={checkboxSx}
                    checked={features.includes(f.id)}
                    onChange={(e) => setFeatures((cur) =>
                      e.target.checked ? [...cur, f.id] : cur.filter((x) => x !== f.id))}
                  />
                }
                label={f.label}
                sx={featureLabelSx}
              />
            ))}
            {features.length === 0 && (
              <Alert severity="error" sx={{ my: 2, borderRadius: '8px' }}>
                At least one protection plan is required. GuardDuty cannot be left
                with none — to stop it entirely, disable GuardDuty from the Danger
                zone.
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={busy || operationRunning || features.length === 0 || !featuresDirty}
              onClick={saveFeatures}
              sx={primaryBtnSx}
            >
              Save features
            </Button>
          </Box>
        )}

        {tab === 1 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Linking regions</Typography>
            <Typography sx={{ ...bodyTextSx, mb: 1 }}>
              Home region: <b>{config.homeRegion}</b> (read-only)
            </Typography>
            <Typography sx={{ ...bodyTextSx, color: CK.muted, mb: 2 }}>
              Adding a region enrolls every member account there; removing a region
              tears down its detectors and prunes its findings.
            </Typography>
            <Box sx={{ mb: 2 }}>
              <MultiSelectField
                label="Linking regions"
                options={AWS_REGIONS.map((r) => ({ value: r.value, label: r.label }))}
                value={regions}
                onChange={setRegions}
                lockedValues={config.homeRegion ? [config.homeRegion] : []}
              />
            </Box>
            {regions.length === 0 && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: '8px' }}>
                At least one linking region is required, and it must include the home
                region <b>{config.homeRegion}</b>.
              </Alert>
            )}
            <Button
              variant="contained"
              disabled={busy || !canUpdateRegions || regions.length === 0 || !regionsDirty}
              onClick={saveRegions}
              sx={primaryBtnSx}
            >
              Save regions
            </Button>
          </Box>
        )}

        {tab === 2 && (
          <Box sx={{ p: 3 }}>
            <RolePermissionsPanel
              startScan={startGDRolesScan}
              getJob={getGDRolesJob}
              fix={fixGDRoles}
              capabilityLabel="GuardDuty"
            />
          </Box>
        )}

        {tab === 3 && (
          <Box sx={{ p: 3 }}>
            <Typography sx={{ ...sectionHeadingSx, mb: 1 }}>Danger zone</Typography>
            <Alert severity="warning" sx={{ mb: 2, borderRadius: '8px' }}>
              Disabling tears down all member enrollments and GuardDuty detectors
              in every region. The findings Prism has already fetched stay visible
              while GuardDuty is off, but re-running setup starts a fresh cache and
              clears them — the detectors they came from no longer exist.
            </Alert>
            <Button color="error" variant="contained" onClick={() => setConfirmOpen(true)} sx={dangerBtnSx}>
              Disable GuardDuty
            </Button>
          </Box>
        )}
      </Paper>
      </motion.div>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)} {...dialogProps}>
        <DialogTitle sx={dialogTitleSx}>Confirm disable</DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography sx={{ ...bodyTextSx, mt: 1, wordBreak: 'break-word' }}>
            Type the customer ID <b>{config.customerId}</b> to confirm:
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Customer ID"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} sx={textBtnSx}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            disabled={busy || confirm !== config.customerId}
            onClick={doDisable}
            sx={dangerBtnSx}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </motion.div>
  );
}
